import {
  AHB_MESSAGE_TYPE,
  AHB_PROCEDURE_ID,
  AHB_PROCESSING_CODE,
  AHB_SUBPROCEDURE,
  AHB_VERSION,
  GUARDIAN,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  TRANSPORTATION_CODE,
} from "core/consts";
import { REHAB_FOCUS_VALUE_TO_CODE } from "onprem/mapping/mapFormToRehabRequest";
import { z } from "zod";
import {
  AHBdateSchema,
  AHBfloorSchema,
  AHBpostleitzahlSchema,
  AHBtimeSchema,
  ICDCodeSchema,
  ICDReasonSchema,
  IKNumberSchema,
} from "./shared";

export const AHBRequestPayloadSchema = z.object({
  Nutzdaten: z.object({
    "@xmlns": z.string(),
    "@xmlns:gdb": z.string(),
    "@xmlns:aar": z.string(),
    "@xmlns:arh": z.string(),
    "@xmlns:feh": z.string(),
    "@xmlns:sto": z.string(),
    Header: z.object({
      "gdb:Absender": z.object({
        "gdb:Krankenhaus": z.object({
          "gdb:Institutionskennzeichen": IKNumberSchema,
        }),
      }),
      "gdb:Empfaenger": z.object({
        "gdb:Krankenkasse": z.object({
          "gdb:Institutionskennzeichen": IKNumberSchema,
        }),
      }),
      "gdb:Erstellungsdatum": AHBdateSchema,
      "gdb:Erstellungszeit": AHBtimeSchema,
      "gdb:Datei_ID": z.string().uuid(),
      "gdb:Verfahrenskennung": z.nativeEnum(AHB_PROCEDURE_ID),
      "gdb:Unterverfahren": z.literal(AHB_SUBPROCEDURE),
      "gdb:Nachrichtentyp": z.literal(AHB_MESSAGE_TYPE.REQUEST),
      "gdb:Logische_Version": z.nativeEnum(AHB_VERSION),
      // "gdb:Dateinummer": ahbDateinummerSchema, #TODO check if required
    }),
    Body: z.object({
      Anschlussrehabilitation: z.object({
        "aar:Antrag": z.object({
          "aar:Versicherter": z.object({
            "gdb:Nachname": z.string().max(45),
            "gdb:Vorname": z.string().max(45),
            "gdb:Titel": z.string().max(20).optional(),
            "gdb:Namenszusatz": z.string().max(20).optional(),
            "gdb:Vorsatzwort": z.string().max(20).optional(),
            "gdb:Strasse": z.string().max(46).optional(),
            "gdb:Hausnummer": z.string().max(9).optional(),
            "gdb:Postleitzahl": AHBpostleitzahlSchema.optional(),
            "gdb:Ort": z.string().max(40).optional(),
            "gdb:Anschriftenzusatz": z.string().max(40).optional(),
            "gdb:Laenderkennzeichen": z.string().max(3).optional(),
            "gdb:Krankenversichertennummer": z.string().min(10).max(12),
            "gdb:Geburtsdatum": AHBdateSchema,
            "gdb:Geschlecht": z.enum(["w", "m", "d", "x"]),
            "gdb:Telefon": z.string().max(25).optional(),
            "gdb:Staatsangehoerigkeit": z.string().optional(),
            "gdb:KH_internes_Kennzeichen": z.string().max(15),
          }),
          "aar:Antrag": z.object({
            "arh:Verarbeitungskennzeichen": z.literal(
              AHB_PROCESSING_CODE.INITIAL_REQUEST,
            ),
            "arh:Angaben_des_Versicherten": z.object({
              "arh:Antrag_Pflegebeduerftigkeit": z.enum(["0", "1"]),
              "arh:Rentenantragstellung": z.object({
                "arh:Altersrente": z.enum(["0", "1"]),
                "arh:Traeger_der_Altersrente": z.string().max(150).optional(),
                "arh:Erwerbsminderungsrente": z.enum(["0", "1"]),
                "arh:Traeger_der_Erwerbsminderungsrente": z
                  .string()
                  .max(150)
                  .optional(),
              }),
              "arh:Haeusliche_Situation": z.object({
                "arh:Stockwerk": AHBfloorSchema(),
                "arh:Aufzug": z.enum(["0", "1"]),
                "arh:Besonderheiten": z.string().max(600).optional(),
              }),
              "arh:Haeusliche_Versorgungssituation": z.object({
                "arh:Wohnsituation": z.enum(["1", "2", "3", "4"]),
                "arh:Versorgung_Sichergestellt": z.enum(["0", "1"]),
              }),
              "arh:Hausarzt": z
                .object({
                  "arh:Nachname": z.string().max(45),
                  "arh:Vorname": z.string().max(45),
                  "arh:Strasse": z.string().max(46).optional(),
                  "arh:Hausnummer": z.string().max(9).optional(),
                  "arh:Postleitzahl": AHBpostleitzahlSchema.optional(),
                  "arh:Ort": z.string().max(40),
                  "arh:Telefon": z.string().max(25).optional(),
                })
                .optional(),
              "arh:Betreuung_Vollmacht_Vertreter": z
                .array(
                  z.object({
                    "arh:Kennzeichen_Vertretung": z.nativeEnum(GUARDIAN),
                    "arh:Nachname": z.string().max(45),
                    "arh:Vorname": z.string().max(45),
                    "arh:Strasse": z.string().max(46).optional(),
                    "arh:Hausnummer": z.string().max(9).optional(),
                    "arh:Postleitzahl": AHBpostleitzahlSchema.optional(),
                    "arh:Ort": z.string().max(40).optional(),
                    "arh:Telefon": z.string().max(25).optional(),
                  }),
                )
                .max(2)
                .optional(),
              "arh:Anforderungen_Wuensche": z.string().max(600).optional(),
            }),
            "arh:Willenserklaerung_Unterschrift": z.literal(1),
            "arh:Entbindung_Schweigepflicht": z.enum(["0", "1"]),
            "arh:Einwilligung_Uebermittlung": z.enum(["0", "1"]),
            "arh:Angaben_des_Krankenhauses": z.object({
              "arh:Standortnummer": z.string().length(9),
              "arh:Ansprechpartner": z.object({
                "arh:Nachname": z.string().max(45),
                "arh:Vorname": z.string().max(45).optional(),
                "arh:Telefon": z.string().max(25),
                "arh:Fax": z.string().max(25).optional(),
              }),
              "arh:Kommunikation": z.object({
                "arh:Deutsche_Sprache": z.enum(["0", "1"]),
                "arh:Sprache": z.string().max(150).optional(),
              }),
              "arh:Rehaeinrichtung": z
                .object({
                  "arh:Rehaeinrichtung_nicht_angefragt": z
                    .literal(1)
                    .optional(),
                  "arh:Angefragte_Rehaeinrichtung": z
                    .object({
                      "arh:Institutionskennzeichen": z
                        .string()
                        .max(9)
                        .optional(),
                      "arh:Anschrift_Rehaeinrichtung": z
                        .object({
                          "arh:Name": z.string().max(45),
                          "arh:Strasse": z.string().max(46),
                          "arh:Hausnummer": z.string().max(9),
                          "arh:Postleitzahl": z.string().max(10),
                          "arh:Ort": z.string().max(40),
                          "arh:Telefon": z.string().max(25),
                        })
                        .optional(),
                    })
                    .optional(),
                  "arh:Fruehestmoegliche_Aufnahme": AHBdateSchema.optional(),
                })
                .superRefine((val, ctx) => {
                  const hasNichtAngefragt =
                    val["arh:Rehaeinrichtung_nicht_angefragt"] === 1;
                  const angefragt = val["arh:Angefragte_Rehaeinrichtung"];
                  const aufnahme = val["arh:Fruehestmoegliche_Aufnahme"];

                  if (hasNichtAngefragt && angefragt) {
                    ctx.addIssue({
                      path: ["arh:Rehaeinrichtung_nicht_angefragt"],
                      code: z.ZodIssueCode.custom,
                      message:
                        "Entweder 'Rehaeinrichtung_nicht_angefragt' oder 'Angefragte_Rehaeinrichtung' darf gesetzt sein – nicht beides.",
                    });
                  }

                  if (!hasNichtAngefragt && !angefragt) {
                    ctx.addIssue({
                      path: ["arh:Angefragte_Rehaeinrichtung"],
                      code: z.ZodIssueCode.custom,
                      message:
                        "Es muss entweder 'Rehaeinrichtung_nicht_angefragt' oder 'Angefragte_Rehaeinrichtung' gesetzt sein.",
                    });
                  }

                  if (angefragt) {
                    const hasIK = !!angefragt["arh:Institutionskennzeichen"];
                    const hasAnschrift =
                      !!angefragt["arh:Anschrift_Rehaeinrichtung"];

                    if (!hasIK && !hasAnschrift) {
                      ctx.addIssue({
                        path: ["arh:Angefragte_Rehaeinrichtung"],
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Angefragte_Rehaeinrichtung' muss entweder 'Institutionskennzeichen' oder 'Anschrift_Rehaeinrichtung' enthalten.",
                      });
                    }

                    if (hasIK && hasAnschrift) {
                      ctx.addIssue({
                        path: ["arh:Angefragte_Rehaeinrichtung"],
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Angefragte_Rehaeinrichtung' darf nicht gleichzeitig 'Institutionskennzeichen' und 'Anschrift_Rehaeinrichtung' enthalten.",
                      });
                    }

                    if (!aufnahme) {
                      ctx.addIssue({
                        path: ["arh:Fruehestmoegliche_Aufnahme"],
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Fruehestmoegliche_Aufnahme' ist erforderlich, wenn eine Rehaeinrichtung angefragt wurde.",
                      });
                    }
                  }
                }),
              "arh:Krankenhausbehandlung": z.object({
                "arh:Beginn": AHBdateSchema,
                "arh:Ende": AHBdateSchema,
                "arh:Version_ICD_OPS": z.string().length(4),
              }),
            }),
            "arh:Fruehrehabilitation": z.enum(["0", "1"]),
            "arh:Verlegung": z
              .object({
                "arh:Kennzeichen_Verlegung": z.nativeEnum(REHAB_ADMISSION),
                "arh:Datum_Verlegung": AHBdateSchema.optional(),
                "arh:Begruendung": z.string().max(600).optional(),
              })
              .superRefine((val, ctx) => {
                const kennzeichen = val["arh:Kennzeichen_Verlegung"];

                if (
                  (kennzeichen === REHAB_ADMISSION.WITHIN_TWO_WEEKS ||
                    kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS) &&
                  !val["arh:Datum_Verlegung"]
                ) {
                  ctx.addIssue({
                    path: ["arh:Datum_Verlegung"],
                    code: z.ZodIssueCode.custom,
                    message:
                      "Datum_Verlegung is required when Kennzeichen_Verlegung is '2' or '3'.",
                  });
                }

                if (
                  kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
                  !val["arh:Begruendung"]
                ) {
                  ctx.addIssue({
                    path: ["arh:Begruendung"],
                    code: z.ZodIssueCode.custom,
                    message:
                      "Begruendung is required when Kennzeichen_Verlegung is '3'.",
                  });
                }
              }),
            "arh:Funktionsdiagnosen": z.object({
              "arh:Funktionsdiagnose": z
                .array(
                  z.object({
                    "arh:Lfd_Nr": z.enum(["1", "2", "3", "4", "5"]),
                    "arh:Diagnose": z.string().max(150),
                    "arh:ICD_10_GM": ICDCodeSchema,
                    "arh:Ursache": ICDReasonSchema,
                  }),
                )
                .min(1)
                .max(5),
            }),
            "arh:Behandlungen": z.object({
              "arh:Operation_durchgefuehrt": z.enum(["0", "1"]),
              "arh:Operationen": z
                .object({
                  "arh:Operation": z
                    .array(
                      z.object({
                        "arh:Wundheilung": z.enum(["0", "1"]),
                        "arh:OPS_Codes": z.object({
                          "arh:OPS": z.array(z.string().max(8)),
                        }),
                        "arh:OP_Datum": AHBdateSchema,
                      }),
                    )
                    .min(1)
                    .max(5),
                })
                .nullable()
                .optional(),
              "arh:Andere_Behandlung": z.string().max(150).optional(),
            }),
            "arh:Komplikationen": z
              .object({
                "arh:Komplikationen_aufgetreten": z.enum(["0", "1"]),
                "arh:Art_der_Komplikation": z
                  .object({
                    "arh:Komplikation_mit_AR_Diagnose": z
                      .string()
                      .max(150)
                      .optional(),
                    "arh:Kardiovaskulaere_Komplikation": z
                      .string()
                      .max(150)
                      .optional(),
                    "arh:Sonstige_Komplikation": z.string().max(150).optional(),
                  })
                  .partial()
                  .optional(),
                "arh:Keime": z
                  .object({
                    "arh:Keimbesiedelung": z.enum(["0", "1", "2"]),
                    "arh:Art_der_Keime": z.string().max(150).optional(),
                  })
                  .optional(),
              })
              .superRefine((val, ctx) => {
                const komplikationen = val["arh:Komplikationen_aufgetreten"];
                const keime = val["arh:Keime"];

                // ✅ Rule 1: Art_der_Komplikation is only allowed if complications occurred
                if (komplikationen === "0" && val["arh:Art_der_Komplikation"]) {
                  ctx.addIssue({
                    path: ["arh:Art_der_Komplikation"],
                    code: z.ZodIssueCode.custom,
                    message:
                      "Art der Komplikation darf nur gesetzt werden, wenn Komplikationen aufgetreten sind.",
                  });
                }

                // ✅ Rule 2: If Komplikationen = "1", then at least one Komplikation description must be provided
                if (komplikationen === "1") {
                  const art = val["arh:Art_der_Komplikation"];
                  const hasAny =
                    !!art?.["arh:Komplikation_mit_AR_Diagnose"] ||
                    !!art?.["arh:Kardiovaskulaere_Komplikation"] ||
                    !!art?.["arh:Sonstige_Komplikation"];

                  if (!hasAny) {
                    ctx.addIssue({
                      path: ["arh:Art_der_Komplikation"],
                      code: z.ZodIssueCode.custom,
                      message:
                        "Mindestens ein Feld unter Art der Komplikation muss ausgefüllt sein.",
                    });
                  }
                }

                // ✅ Rule 3: Keime (infections) is only required if complications occurred
                if (komplikationen === "0" && keime) {
                  ctx.addIssue({
                    path: ["arh:Keime"],
                    code: z.ZodIssueCode.custom,
                    message:
                      "Angaben zu Keimen dürfen nur gemacht werden, wenn Komplikationen aufgetreten sind.",
                  });
                }

                // ✅ Rule 4: If Keimbesiedelung = "1", then Art_der_Keime must be provided
                if (
                  komplikationen === "1" &&
                  keime?.["arh:Keimbesiedelung"] === "1" &&
                  !keime?.["arh:Art_der_Keime"]
                ) {
                  ctx.addIssue({
                    path: ["arh:Keime", "arh:Art_der_Keime"],
                    code: z.ZodIssueCode.custom,
                    message:
                      "Art der Keime ist erforderlich, wenn Keimbesiedelung mit multiresistenten Keimen vorliegt.",
                  });
                }
              }),
            "arh:Alltagsrelevante_Beeintraechtigungen": z.object({
              "arh:Beeintraechtigung": z
                .array(
                  z.object({
                    "arh:Item": z.string().length(2),
                    "arh:Punkte": z.number().int().min(0).max(5),
                  }),
                )
                .max(20),
              "arh:Gesamtpunkte": z.number().int().min(0).max(100),
            }),
            "arh:Rehabilitationsfaehigkeit": z
              .object({
                "arh:Ausreichende_Therapiebelastbarkeit": z.enum(["0", "1"]),
                "arh:Belastbarkeit_nach_OP": z.enum(["1", "2", "3"]).optional(),
                "arh:Teilbelastbar_mit": z.number().int().max(999).optional(),
                "arh:Anpassung_Interimsprothese_erfolgt": z.enum(["0", "1"]),
                "arh:Besonderheiten": z.string().max(150).optional(),
                "arh:Kriterien_FR_Index": z
                  .object({
                    "arh:Kriterium": z
                      .array(z.number().int().max(7))
                      .min(1)
                      .max(7),
                  })
                  .optional(),
              })
              .superRefine((data, ctx) => {
                // If Belastbarkeit_nach_OP is "2", Teilbelastbar_mit must be provided
                if (data["arh:Belastbarkeit_nach_OP"] === "2") {
                  if (data["arh:Teilbelastbar_mit"] === undefined) {
                    ctx.addIssue({
                      path: ["arh:Teilbelastbar_mit"],
                      code: z.ZodIssueCode.custom,
                      message:
                        '"Teilbelastbar_mit" is required when Belastbarkeit_nach_OP is "2" (teilweise belastbar)',
                    });
                  }
                }
              }),
            "arh:Rehabilitationsziel": z.string().max(600),
            "arh:Zusammenfassende_Bewertung": z
              .object({
                "arh:Schwerpunkt": z
                  .object({
                    "arh:Rehaschwerpunkt": z.string().regex(/^\d{2}$/),
                    "arh:Erlaeuterung": z.string().max(600).optional(),
                  })
                  .refine(
                    (data) =>
                      data["arh:Rehaschwerpunkt"] !==
                        REHAB_FOCUS_VALUE_TO_CODE[REHAB_FOCUS.OTHER] ||
                      !!data["arh:Erlaeuterung"],
                    {
                      message: `"arh:Erlaeuterung" is required when "arh:Rehaschwerpunkt" is "${String(
                        REHAB_FOCUS.OTHER,
                      )}"`,
                      path: ["arh:Erlaeuterung"],
                    },
                  ),
                "arh:Empfohlene_Rehabilitationsart": z.union([
                  z.literal(1),
                  z.literal(2),
                  z.literal(4),
                ]),
                "arh:Begruendung_Rehabilitationsart": z
                  .string()
                  .max(150)
                  .optional(),
                "arh:Fall_Paragraph_16": z.enum(["0", "1"]),
                "arh:Begruendungen": z
                  .object({
                    "arh:Begruendung": z
                      .array(z.nativeEnum(REHAB_DIRECTIVE_TYPE))
                      .min(1)
                      .max(12),
                  })
                  .optional(),
              })
              .refine(
                (data) =>
                  data["arh:Fall_Paragraph_16"] !== "1" ||
                  (data["arh:Begruendungen"] &&
                    data["arh:Begruendungen"]["arh:Begruendung"].length > 0),
                {
                  message: `"arh:Begruendungen" is required when "arh:Fall_Paragraph_16" is "1"`,
                  path: ["arh:Begruendungen"],
                },
              ),
            "arh:Weitere_Informationen": z.object({
              "arh:Besondere_Anforderungen": z.object({
                "arh:Keine_besonderen_Anforderungen": z.literal("1").optional(),
                "arh:Anforderungen": z
                  .object({
                    "arh:Anforderung": z
                      .array(
                        z.string().regex(/^(0[1-9]|1[0-7])$/, {
                          message: "Must be a number between 01 and 17",
                        }),
                      )
                      .min(1)
                      .max(17),
                    "arh:Dialyseverfahren": z.string().max(150).optional(),
                    "arh:Erlaeuterung": z.string().max(600).optional(),
                    "arh:Pflegegrad_Angehoeriger": z
                      .enum(["1", "2", "3", "4", "5"])
                      .optional(),
                    "arh:Grad_Dekubitus": z
                      .enum(["1", "2", "3", "4"])
                      .optional(),
                    "arh:Last_bis": z.number().int().max(999).optional(),
                  })
                  .optional(),
              }),
              "arh:Anreise": z.object({
                "arh:Verkehrsmittel": z.nativeEnum(TRANSPORTATION_CODE),
                "arh:Begruendung_KTW": z.string().max(150).optional(),
                "arh:Begleitperson": z.enum(["0", "1"]),
              }),
              "arh:Krankenhausarzt": z.object({
                "arh:Nachname": z.string().max(45),
                "arh:Vorname": z.string().max(45),
                "arh:Titel": z.string().max(20).optional(),
                "arh:Telefon": z.string().max(25),
                "arh:Datum": AHBdateSchema,
              }),
            }),
          }),
        }),
      }),
    }),
  }),
});
